import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useCallState } from './CallProvider';
import { useParticipants } from './ParticipantsProvider';
import { useUIState } from './UIStateProvider';
import {
  CALL_STATE_REDIRECTING,
  CALL_STATE_JOINED,
} from './useCallMachine';
import PropTypes from 'prop-types';
import { useDeepCompareEffect } from 'use-deep-compare';

export const RECORDING_ERROR = 'error';
export const RECORDING_SAVED = 'saved';
export const RECORDING_RECORDING = 'recording';
export const RECORDING_UPLOADING = 'uploading';
export const RECORDING_COUNTDOWN_1 = 'starting1';
export const RECORDING_COUNTDOWN_2 = 'starting2';
export const RECORDING_COUNTDOWN_3 = 'starting3';
export const RECORDING_IDLE = 'idle';

export const RECORDING_TYPE_CLOUD = 'cloud';
export const RECORDING_TYPE_CLOUD_BETA = 'cloud-beta';
export const RECORDING_TYPE_LOCAL = 'local';
export const RECORDING_TYPE_OUTPUT_BYTE_STREAM = 'output-byte-stream';
export const RECORDING_TYPE_RTP_TRACKS = 'rtp-tracks';

const RecordingContext = createContext({
  isRecordingLocally: false,
  recordingStartedDate: null,
  recordingState: RECORDING_IDLE,
  startRecording: null,
  stopRecording: null,
});

export const RecordingProvider = ({ children }) => {
  const {
    callObject,
    enableRecording,
    startCloudRecording,
    state,
    leave,
    saveLevel,
  } = useCallState();

// const enableRecording = 'rtp-tracks';

  const { participants } = useParticipants();
  const [recordingStartedDate, setRecordingStartedDate] = useState(null);
  const [recordingState, setRecordingState] = useState(RECORDING_IDLE);
  const [isRecordingLocally, setIsRecordingLocally] = useState(false);
  const [hasRecordingStarted, setHasRecordingStarted] = useState(false);
  const { setCustomCapsule, closeModal, setShowAside } = useUIState();

  const handleOnUnload = useCallback(
    () => 'Unsaved recording in progress. Do you really want to leave?',
    []
  );

  useEffect(() => {
    if (
      !enableRecording ||
      !isRecordingLocally ||
      recordingState !== RECORDING_RECORDING ||
      state === CALL_STATE_REDIRECTING
    ) return;

    const prev = window.onbeforeunload;
    window.onbeforeunload = handleOnUnload;
    return () => {
      window.onbeforeunload = prev;
    };
  }, [
    enableRecording,
    handleOnUnload,
    recordingState,
    isRecordingLocally,
    state,
  ]);

  useEffect(() => {
    if (!callObject || !enableRecording) return;

    const handleAppMessage = (ev) => {
      switch (ev?.data?.event) {
        case 'recording-starting':
          setRecordingState(RECORDING_COUNTDOWN_3);
          break;
        default:
          break;
      }
    };

    // The 'recording-data' event is emitted when an output-byte-stream recording has started
    // When the event emits, start writing data to the stream created in handleRecordingStarted()
    const handleRecordingData = async (ev) => {
      try {
        console.log('got data', ev);
        await window.writer.write(ev.data);
        if (ev.finished) {
          console.log('closing!');
          window.writer.close();
        }
      } catch (e) {
        console.error(e);
      }
    };

    callObject.on('app-message', handleAppMessage);
    callObject.on('recording-data', handleRecordingData);

    return () => {
      callObject.off('app-message', handleAppMessage);
      callObject.off('recording-data', handleRecordingData);
    };
  }, [callObject, enableRecording]);

  // Reset recording state when leaving the call so a subsequent join
  // (e.g. "New call") can auto-start recording again.
  useEffect(() => {
    if (state === CALL_STATE_JOINED) return;
    setHasRecordingStarted(false);
    setIsRecordingLocally(false);
    setRecordingState(RECORDING_IDLE);
    setRecordingStartedDate(null);
  }, [state]);

  /**
   * Automatically start cloud recording, if startCloudRecording is set.
   */
useEffect(() => {
  if (
    hasRecordingStarted ||
    !callObject ||
    state !== CALL_STATE_JOINED
  ) return;

  const timeout = setTimeout(() => {
    const isSomebodyRecording = participants.some((p) => p.isRecording);
    if (!isSomebodyRecording) {
      // saveLevel=nothing: don't start Daily's capture at all. Otherwise start it — raw-tracks has
      // no per-track audio/video toggle, so audio/transcription's video-drop is enforced server-side.
      if (saveLevel !== 'nothing') {
        callObject.startRecording({ type: 'raw-tracks' });
        setIsRecordingLocally(true);
      }
      setHasRecordingStarted(true);
    } else {
      setHasRecordingStarted(true);
    }
  }, 500);

  return () => {
    clearTimeout(timeout);
  };
}, [callObject, hasRecordingStarted, participants, state, saveLevel]);

// Mid-call saveLevel change: the only thing worth touching live is whether Daily is recording at
// all (into/out of 'nothing') — video/audio/transcription capture identically, kept/dropped server-side.
const prevSaveLevelRef = React.useRef(saveLevel);
useEffect(() => {
  const prev = prevSaveLevelRef.current;
  prevSaveLevelRef.current = saveLevel;
  if (prev === saveLevel || !callObject || !hasRecordingStarted) return;

  const prevNothing = prev === 'nothing';
  const nextNothing = saveLevel === 'nothing';
  if (prevNothing === nextNothing) return;

  if (nextNothing) {
    // Stop live capture; handleRecordingStopped below checks the shared saveLevel so this stop
    // doesn't read as the call itself ending, for every participant.
    if (isRecordingLocally) {
      Promise.resolve(callObject.stopRecording()).catch((e) => console.error(e));
      setIsRecordingLocally(false);
    }
    return;
  }

  // Resuming from a genuine "record nothing" stretch — nothing to carry over, just start fresh.
  callObject.startRecording({ type: 'raw-tracks' });
  setIsRecordingLocally(true);
}, [saveLevel, callObject, hasRecordingStarted, isRecordingLocally]);


  /**
   * Handle participant updates to sync recording state.
   */
  // useDeepCompareEffect(() => {
  //   if (isRecordingLocally || recordingState === RECORDING_SAVED) return;
  //   if (participants.some(({ isRecording }) => isRecording)) {
  //     setRecordingState(RECORDING_RECORDING);
  //   } else {
  //     setRecordingState(RECORDING_IDLE);
  //   }
  // }, [isRecordingLocally, participants, recordingState]);

  /**
   * Handle recording started.
   */
  const handleRecordingStarted = useCallback(
    (event) => {
      console.log(event);

      if (recordingState === RECORDING_RECORDING) return;
      setRecordingState(RECORDING_RECORDING);
      if (event.local) {
        // Recording started locally, either through UI or programmatically
        setIsRecordingLocally(true);
        if (!recordingStartedDate) setRecordingStartedDate(new Date());
        // If an output-byte-stream recording has started, create a new data stream that can be piped to a third-party (in this case a file)
/*
        if (event.type === 'output-byte-stream') {
          const { readable, writable } = new TransformStream({
            transform: (chunk, ctrl) => {
              chunk.arrayBuffer().then((b) => ctrl.enqueue(new Uint8Array(b)));
            },
          });
          window.writer = writable.getWriter();
          readable.pipeTo(window.streamSaver.createWriteStream('test-vid.mp4'));
        }
*/
      }
    },
    [recordingState, recordingStartedDate]
  );

  useEffect(() => {
    if (!callObject || !enableRecording) return;

    callObject.on('recording-started', handleRecordingStarted);
    return () => callObject.off('recording-started', handleRecordingStarted);
  }, [callObject, enableRecording, handleRecordingStarted]);

  /**
   * Handle recording stopped.
   */
  useEffect(() => {
    if (!callObject || !enableRecording) return;

    const handleRecordingStopped = (event) => {
      // if (isRecordingLocally) return;
      setRecordingState(RECORDING_IDLE);
      setRecordingStartedDate(null);

      // A stop from switching to 'nothing' is expected — don't leave. saveLevel is shared, so this
      // holds for everyone, not just whoever changed it.
      if (saveLevel === 'nothing') return;

      leave();
      setShowAside(null);
      closeModal(null);

      // fetchHandler( // test // todo: remove
      //   `${process.env.DAILY_REST_DOMAIN || 'https://api.daily.co/v1'}/recordings`,
      //   {
      //     method: 'GET',
      //     headers: {
      //       'Content-Type': 'application/json',
      //       Authorization: `Bearer ${process.env.DAILY_API_KEY}`,
      //     },
      //   }
      // );

    };

    callObject.on('recording-stopped', handleRecordingStopped);

    return () => callObject.off('recording-stopped', handleRecordingStopped);
  }, [callObject, enableRecording, isRecordingLocally, saveLevel, leave, setShowAside, closeModal]);

  /**
   * Handle recording error.
   */
  const handleRecordingError = useCallback(() => {
    if (isRecordingLocally) setRecordingState(RECORDING_ERROR);
    setIsRecordingLocally(false);
  }, [isRecordingLocally]);

  useEffect(() => {
    if (!callObject || !enableRecording) return;

    callObject.on('recording-error', handleRecordingError);
    return () => callObject.off('recording-error', handleRecordingError);
  }, [callObject, enableRecording, handleRecordingError]);

  const startRecording = useCallback(() => {
    if (!callObject || !isRecordingLocally) return;
    callObject.startRecording();
  }, [callObject, isRecordingLocally]);

  useEffect(() => {
    let timeout;
    switch (recordingState) {
      case RECORDING_COUNTDOWN_3:
        timeout = setTimeout(() => {
          setRecordingState(RECORDING_COUNTDOWN_2);
        }, 1000);
        break;
      case RECORDING_COUNTDOWN_2:
        timeout = setTimeout(() => {
          setRecordingState(RECORDING_COUNTDOWN_1);
        }, 1000);
        break;
      case RECORDING_COUNTDOWN_1:
        startRecording();
        break;
      case RECORDING_ERROR:
      case RECORDING_SAVED:
        timeout = setTimeout(() => {
          setRecordingState(RECORDING_IDLE);
          setIsRecordingLocally(false);
        }, 5000);
        break;
      default:
        break;
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [recordingState, startRecording]);

  // Show a custom capsule when recording in progress
  useEffect(() => {
    if (recordingState !== RECORDING_RECORDING) {
      setCustomCapsule(null);
    } else {
      setCustomCapsule({ variant: 'recording', label: 'Recording' });
    }
  }, [recordingState, setCustomCapsule]);

  const startRecordingWithCountdown = useCallback(() => {
    if (!callObject || !enableRecording) return;
    setIsRecordingLocally(true);
    setRecordingState(RECORDING_COUNTDOWN_3);
    callObject.sendAppMessage({
      event: 'recording-starting',
    });
  }, [callObject, enableRecording]);

  const stopRecording = useCallback(() => {
    // Nothing to stop
    if (!callObject || !enableRecording) return Promise.resolve();
    switch (enableRecording) {
      case RECORDING_TYPE_LOCAL:
      case RECORDING_TYPE_OUTPUT_BYTE_STREAM:
        setRecordingState(RECORDING_SAVED);
        setIsRecordingLocally(false);
        break;
      case RECORDING_TYPE_CLOUD:
      case RECORDING_TYPE_CLOUD_BETA:
      case RECORDING_TYPE_RTP_TRACKS:
        setRecordingState(RECORDING_UPLOADING);
        setRecordingState(RECORDING_SAVED);
        break;
      default:
        break;
    }
    setRecordingStartedDate(null);
    // Guard against the daily-js promise rejecting (e.g. no active recording);
    // callers don't .catch(), so swallow it here to avoid unhandled rejections.
    return Promise.resolve(callObject.stopRecording()).catch((e) => {
      console.error(e);
    });
  }, [callObject, enableRecording]);

  return (
    <RecordingContext.Provider
      value={{
        isRecordingLocally,
        recordingStartedDate,
        recordingState,
        startRecordingWithCountdown,
        stopRecording,
      }}
    >
      {children}
    </RecordingContext.Provider>
  );
};

RecordingProvider.propTypes = {
  children: PropTypes.node,
};

export const useRecording = () => useContext(RecordingContext);
